import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Rules() {
  const qc = useQueryClient();
  const [rule, setRule] = useState({ keyword: "", category: "", type: "expense" });
  const [cat, setCat] = useState({ name: "", type: "expense" });

  const { data: rules = [] } = useQuery({ queryKey: ["rules"], queryFn: () => api.get("/rules").then((r) => r.data) });
  const { data: incomeCats = [] } = useQuery({ queryKey: ["categories", "income"], queryFn: () => api.get("/categories?type=income").then((r) => r.data) });
  const { data: expenseCats = [] } = useQuery({ queryKey: ["categories", "expense"], queryFn: () => api.get("/categories?type=expense").then((r) => r.data) });

  const allCats = rule.type === "income" ? incomeCats : expenseCats;

  const addRule = async () => {
    if (!rule.keyword.trim() || !rule.category) return toast.error("Preencha palavra-chave e categoria");
    try {
      await api.post("/rules", rule);
      toast.success("Regra criada");
      setRule({ keyword: "", category: "", type: rule.type });
      qc.invalidateQueries({ queryKey: ["rules"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const delRule = async (id) => { await api.delete(`/rules/${id}`); qc.invalidateQueries({ queryKey: ["rules"] }); };

  const addCat = async () => {
    if (!cat.name.trim()) return toast.error("Indique o nome");
    try {
      await api.post("/categories", cat);
      toast.success("Categoria criada");
      setCat({ name: "", type: cat.type });
      qc.invalidateQueries({ queryKey: ["categories", cat.type] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const delCat = async (c) => { await api.delete(`/categories/${c.id}`); qc.invalidateQueries({ queryKey: ["categories", c.type] }); };

  return (
    <div className="space-y-5 max-w-3xl">
      <h1 className="font-heading text-2xl font-bold">Regras & Categorias</h1>

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-rules">Regras</TabsTrigger>
          <TabsTrigger value="cats" data-testid="tab-cats">Categorias</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            As suas regras têm <b>prioridade absoluta</b>. Se a palavra-chave surgir na descrição ou remetente, a categoria é aplicada automaticamente.
          </p>
          <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-1">
              <Label>Palavra-chave</Label>
              <Input value={rule.keyword} onChange={(e) => setRule({ ...rule, keyword: e.target.value })} placeholder="ex.: galp" data-testid="rule-keyword" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={rule.type} onValueChange={(v) => setRule({ ...rule, type: v, category: "" })}>
                <SelectTrigger data-testid="rule-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Despesa</SelectItem>
                  <SelectItem value="income">Recebido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={rule.category} onValueChange={(v) => setRule({ ...rule, category: v })}>
                <SelectTrigger data-testid="rule-category"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {allCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addRule} className="rounded-full" data-testid="rule-add"><Plus size={16} className="mr-1" /> Adicionar</Button>
          </div>

          <div className="bg-card border border-border rounded-lg divide-y divide-border" data-testid="rules-list">
            {rules.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Sem regras definidas.</p> :
              rules.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3" data-testid={`rule-${r.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono bg-secondary px-2 py-0.5 rounded">{r.keyword}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-medium">{r.category}</span>
                    {r.type && <Badge variant="outline">{r.type === "income" ? "recebido" : "despesa"}</Badge>}
                  </div>
                  <button onClick={() => delRule(r.id)} className="text-muted-foreground hover:text-[#C75D42] p-1" data-testid={`rule-delete-${r.id}`}><Trash2 size={16} /></button>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="cats" className="space-y-4">
          <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label>Nome da categoria</Label>
              <Input value={cat.name} onChange={(e) => setCat({ ...cat, name: e.target.value })} data-testid="cat-name" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={cat.type} onValueChange={(v) => setCat({ ...cat, type: v })}>
                <SelectTrigger data-testid="cat-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Despesa</SelectItem>
                  <SelectItem value="income">Recebido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addCat} className="rounded-full" data-testid="cat-add"><Plus size={16} className="mr-1" /> Adicionar</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[{ title: "Recebido", list: incomeCats }, { title: "Despesa", list: expenseCats }].map((g) => (
              <div key={g.title} className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-2 font-heading">{g.title}</h3>
                <div className="flex flex-wrap gap-2">
                  {g.list.map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1 bg-secondary rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`cat-chip-${c.name}`}>
                      {c.name}
                      <button onClick={() => delCat(c)} className="text-muted-foreground hover:text-[#C75D42] rounded-full p-0.5"><Trash2 size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
